import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { getToken } from "./tokenStorage";
import { AuthCtx } from "./AuthProvider";

const CANDY_ROUTES = ["/schedule", "/auditories", "/account", "/users", "/teacher-export"];
const SPRITES = ["/candy_1.svg", "/candy_2.svg", "/candy_3.svg"];

function normalizeRoute(pathname) {
  if (!pathname) return "/";
  if (pathname.startsWith("/profile/")) return "/profile";
  return pathname;
}

function routeToZone(route) {
  const slug = route.replace(/^\//, "").replace(/[\/-]/g, "_");
  return `candy_${slug}`;
}

function hashStr(s) {
  let h = 0;
  for (const c of s) h = ((h * 31 + c.charCodeAt(0)) >>> 0);
  return h;
}

function pickSprite(windowId, userId, zoneId) {
  return SPRITES[hashStr(windowId + userId + zoneId) % SPRITES.length];
}

function pickPosition(windowId, userId, zoneId) {
  const h1 = hashStr("x" + windowId + userId + zoneId);
  const h2 = hashStr("y" + windowId + userId + zoneId);
  const left = 8 + (h1 % 80);
  const top = 20 + (h2 % 60);
  return { left: `${left}%`, top: `${top}%` };
}

async function fetchWindow() {
  const token = getToken();
  if (!token) return null;
  try {
    const r = await fetch("/candy/window", { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

async function fetchClaimed(windowId) {
  const token = getToken();
  if (!token) return new Set();
  try {
    const r = await fetch(`/candy/claimed-today?window_id=${encodeURIComponent(windowId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return new Set();
    const data = await r.json();
    return new Set(data.zones || []);
  } catch {
    return new Set();
  }
}

async function collectCandy(zoneId) {
  const token = getToken();
  if (!token) return null;
  try {
    const r = await fetch("/candy/collect", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ zone_id: zoneId }),
    });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

function playChomp() {
  try {
    const audio = new Audio("/chomp-1.mp3");
    audio.volume = 0.6;
    audio.playbackRate = 0.9 + Math.random() * 0.2;
    audio.play().catch(() => {});
  } catch {}
}

function CandyPickup({ sprite, position, onCollect }) {
  const [collecting, setCollecting] = useState(false);
  const [gone, setGone] = useState(false);

  const handleClick = async () => {
    if (collecting) return;
    setCollecting(true);
    playChomp();
    const result = await onCollect();
    if (result && !result.already_collected && !result.debug) {
      window.dispatchEvent(new CustomEvent("candyCollected", { detail: result }));
    }
    setGone(true);
  };

  if (gone) return null;

  return (
    <button
      type="button"
      aria-label="Собрать конфету"
      onClick={handleClick}
      className="fixed z-[60] cursor-pointer border-0 bg-transparent p-0 transition-transform duration-150 hover:scale-110 active:scale-95"
      style={{
        ...position,
        filter: "drop-shadow(0 0 6px rgba(255,146,40,0.75)) drop-shadow(0 0 12px rgba(255,100,10,0.4))",
      }}
    >
      <img src={sprite} alt="" className="pointer-events-none select-none" style={{ width: 48, height: "auto", imageRendering: "pixelated" }} />
    </button>
  );
}

const DEBUG_ZONES_BY_ROUTE = {
  "/schedule": ["candy_schedule"],
  "/auditories": ["candy_auditories"],
  "/account": ["candy_account_shop", "candy_account_collection"],
  "/users": ["candy_users"],
  "/teacher-export": ["candy_teacher_export"],
};

export function DailyCandyGlobal({ route }) {
  const { user } = useContext(AuthCtx);
  const [windowInfo, setWindowInfo] = useState(null);
  const [claimed, setClaimed] = useState(() => new Set());
  const [ready, setReady] = useState(false);
  const [debugTick, setDebugTick] = useState(0);
  const pollRef = useRef(null);

  const refreshAll = useCallback(async () => {
    const wi = await fetchWindow();
    let cl = new Set();
    if (wi?.window_id) {
      cl = await fetchClaimed(wi.window_id);
    }
    // Все setState одним батчем после обоих fetch — иначе окно рендерится с пустым claimed
    // и собранные конфеты моргают до того как fetchClaimed вернётся
    setWindowInfo(wi);
    setClaimed(cl);
    setReady(true);
  }, []);

  useEffect(() => {
    if (!user) { setReady(true); return; }
    refreshAll();
    pollRef.current = setInterval(refreshAll, 60_000);
    const onFocus = () => refreshAll();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      clearInterval(pollRef.current);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [user?.id, refreshAll]);

  useEffect(() => {
    const onDebug = () => setDebugTick(t => t + 1);
    window.addEventListener("debugCandyReset", onDebug);
    return () => window.removeEventListener("debugCandyReset", onDebug);
  }, []);

  if (!user) return null;

  const normalized = normalizeRoute(route);
  if (!CANDY_ROUTES.includes(normalized)) return null;

  const debugActive = typeof window !== "undefined" && !!window.__debugCandyWindow;

  if (debugActive) {
    const dbgWin = window.__debugCandyWindow;
    const zones = DEBUG_ZONES_BY_ROUTE[normalized] || [];
    return (
      <>
        {zones.map((zId, idx) => {
          const sprite = SPRITES[(hashStr(dbgWin + zId + debugTick) + idx) % SPRITES.length];
          const position = pickPosition(dbgWin + debugTick, user.id, zId);
          return (
            <CandyPickup
              key={`dbg-${idx}-${debugTick}`}
              sprite={sprite}
              position={position}
              onCollect={async () => ({ already_collected: false, debug: true })}
            />
          );
        })}
      </>
    );
  }

  if (!ready || !windowInfo) return null;
  if (!windowInfo.spawn_active) return null;

  const zoneId = routeToZone(normalized);
  const activeZones = new Set(windowInfo.zones || []);

  const render = (zId) => {
    if (!activeZones.has(zId)) return null;
    if (claimed.has(zId)) return null;
    const sprite = pickSprite(windowInfo.window_id, user.id, zId);
    const position = pickPosition(windowInfo.window_id, user.id, zId);
    return (
      <CandyPickup
        key={zId}
        sprite={sprite}
        position={position}
        onCollect={async () => {
          const result = await collectCandy(zId);
          if (result) {
            setClaimed(prev => new Set(prev).add(zId));
          }
          return result;
        }}
      />
    );
  };

  // Account route splits into shop + collection sub-zones (same as skrep)
  if (normalized === "/account") {
    return (
      <>
        {render("candy_account_shop")}
        {render("candy_account_collection")}
      </>
    );
  }

  return render(zoneId);
}
